<script setup lang="ts">
    import { computed, onMounted, ref } from 'vue';
    import { RouterLink, useRoute, useRouter } from 'vue-router';
    import { useGetInteractions } from '../hooks/interaction';
    import { __ } from '../stores/expressions';
    import { getChannelLabel } from '../services/api/interaction/consts';
    import { dateToString } from '../utils/dateUtils';
    import InteractionSummary from '../components/interactions/summary/InteractionSummary.vue';
    import EchoButton from '../components/EchoButton.vue';
    import EchoTooltip from '../components/EchoTooltip.vue';
    import TranscriptionChat from '../components/interactions/transcription/TranscriptionChat.vue';
    import InteractionInfo from '../components/interactions/summary/InteractionInfo.vue';
    import LoadingDots from '../components/LoadingDots.vue';
    import TranscriptionVisualization from '../components/interactions/transcription/TranscriptionVisualization.vue';
    import ToggleSwitch from '../components/ToggleSwitch.vue';
    import { useAudioPlayerStore } from '../stores/audioPlayer';
    import { ChevronLeftIcon } from '@lucide/vue';

    const route = useRoute();
    const router = useRouter();

    onMounted(() => {
        if (!route.params.interactionId) {
            router.push('/interactions');
        }
    });

    const req = computed(() => [route.params.interactionId as string]);

    const { data: interactions, isLoading } = useGetInteractions(req);

    const interaction = computed(() => {
        return interactions.value?.interactions[0];
    });

    const currentTime = ref(0);

    const audioPlayerStore = useAudioPlayerStore();

    const channelLabel = computed(() => {
        if (!interaction.value) return '';

        return getChannelLabel(interaction.value.channelName);
    });

    // Prefer returning to wherever the user came from; only the interactions
    // list is a safe fallback when there's no history (e.g. a direct link).
    const goBack = () => {
        if (window.history.length > 1) {
            router.back();
        } else {
            router.push('/interactions');
        }
    };
</script>
<template>
    <div class="page-padding container">
        <template v-if="!isLoading && interaction">
            <div class="relative bg-white rounded-lg shadow-md mt-2 pb-7">
                <div class="px-7 py-8 pb-5">
                    <div class="mb-6 -mt-3 flex items-center gap-4">
                        <EchoTooltip :label="__('interaction-back-button')">
                            <button
                                type="button"
                                class="Icon-button rounded-full border border-gray-light bg-white hover:bg-gray-lightest"
                                :aria-label="__('interaction-back-button')"
                                @click="goBack"
                            >
                                <ChevronLeftIcon class="size-5" />
                            </button>
                        </EchoTooltip>
                        <nav class="flex items-center gap-2 min-w-0 text-body text-gray-medium">
                            <RouterLink to="/interactions" class="hover:text-primary-beet">
                                {{ __('interactions-breadcrumb') }}
                            </RouterLink>
                            <span>/</span>
                            <span class="text-body-bold text-gray-dark truncate">
                                {{ channelLabel }} · {{ interaction.agentName }} ·
                                {{ dateToString(new Date(interaction.startTime), null, false) }}
                            </span>
                        </nav>
                        <div class="ml-auto">
                            <EchoButton
                                variant="primary"
                                :route="'/quality-assurance/' + route.params.interactionId"
                            >
                                {{ __('interactions-coach-now') }}
                            </EchoButton>
                        </div>
                    </div>
                    <div class="border-b border-gray-lightest pb-5">
                        <InteractionInfo :interaction="interaction" />
                    </div>
                    <div class="mt-5">
                        <TranscriptionVisualization
                            :interaction="interaction"
                            @current-time-update="currentTime = $event"
                        />
                    </div>
                </div>
                <div class="flex items-start gap-5 mx-7">
                    <div
                        class="flex-1 min-w-0 border border-gray-light rounded-lg p-6"
                        :class="interaction.summary?.summary ? 'flex-4' : ''"
                    >
                        <div :class="interaction.summary?.summary ? '' : 'max-w-225 mx-auto'">
                            <div class="flex items-center justify-between mb-4 pr-2">
                                <h2 class="heading-md">
                                    {{ __('transcription') }}
                                </h2>
                                <div class="flex items-center gap-5">
                                    <ToggleSwitch
                                        v-if="audioPlayerStore.hasPlayerFor(interaction.id)"
                                        v-model="audioPlayerStore.autoScrollEnabled"
                                        class="group"
                                        :label="__('transcription-enable-auto-scroll')"
                                    />
                                </div>
                            </div>
                            <TranscriptionChat
                                :interaction="interaction"
                                :current-time="currentTime"
                                :enable-keyword-filter="false"
                            />
                        </div>
                    </div>
                    <div v-if="interaction.summary?.summary" class="flex-3 min-w-0">
                        <InteractionSummary :summary="interaction.summary" />
                    </div>
                </div>
            </div>
        </template>
        <template v-else>
            <LoadingDots />
        </template>
    </div>
</template>
